import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

type TxnType = 'receipt' | 'payment';

type BankAccount = {
  id: string;
  name?: string | null;
  bankName?: string | null;
};

type UnitOption = {
  id: string;
  unitNumber: string;
  unitAmount?: number | null;
};

type BankTransactionDrawerProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  saveError?: string | null;
  saving?: boolean;
  voucherUploading?: boolean;
  onSave: () => void;
  onSaveAndSend?: (() => void) | null;
  showSaveAndSendButton?: boolean;
  saveAndSendLabel?: string;
  onSkip?: (() => void) | null;
  showSkipButton?: boolean;
  skipButtonLabel?: string;
  onExtraFooterAction?: (() => void) | null;
  showExtraFooterAction?: boolean;
  extraFooterActionLabel?: string;
  onHeaderAction?: (() => void) | null;
  headerActionIcon?: React.ReactNode;
  headerActionTitle?: string;
  customTopSection?: React.ReactNode;
  customPostAmountSection?: React.ReactNode;
  customAfterAmountSection?: React.ReactNode;

  txnType: TxnType;
  setTxnType: (v: TxnType) => void;
  receiptCategory: 'customer' | 'gst' | 'advocate' | 'other';
  setReceiptCategory: (v: 'customer' | 'gst' | 'advocate' | 'other') => void;
  legalChargeType?: '' | 'stamp_duty' | 'registration_charges' | 'agreement_fee' | 'deed_fee';
  setLegalChargeType?: (v: '' | 'stamp_duty' | 'registration_charges' | 'agreement_fee' | 'deed_fee') => void;
  showLegalChargeType?: boolean;
  hideTxnType?: boolean;
  hideReceiptCategory?: boolean;

  eligibleBankAccounts: BankAccount[];
  bankAccountId: string;
  setBankAccountId: (v: string) => void;
  txnDateYMD: string;
  setTxnDateYMD: (v: string) => void;

  amount: string;
  setAmount: (v: string) => void;
  showUnitPicker: boolean;
  eligibleUnitOptions: UnitOption[];
  unitId: string;
  setUnitId: (v: string) => void;
  unitNumber: string;
  setUnitNumber: (v: string) => void;
  pendingAmountText?: string | null;

  partyName: string;
  setPartyName: (v: string) => void;
  partyNameOptions?: string[];
  payerBankName: string;
  setPayerBankName: (v: string) => void;
  payerBankNameOptions?: string[];
  onPayerBankClear?: () => void;

  paymentModeOptions: string[];
  mode: string;
  setMode: (v: string) => void;

  referenceNo: string;
  setReferenceNo: (v: string) => void;
  notes: string;
  setNotes: (v: string) => void;

  voucherInputRef: React.RefObject<HTMLInputElement>;
  voucherFile: File | null;
  setVoucherFile: (f: File | null) => void;
  voucherRequired: boolean;
  existingVoucherUrl?: string | null;
  drawerWidth?: number | string;
  variant?: 'drawer' | 'panel';
  panelSx?: any;
  readOnly?: boolean;
  editableFields?: {
    bank?: boolean;
    amount?: boolean;
    referenceNo?: boolean;
    txnType?: boolean;
    receiptCategory?: boolean;
  };
  onToggleEditField?: (field: 'bank' | 'amount' | 'referenceNo' | 'txnType' | 'receiptCategory') => void;
};

export default function BankTransactionDrawer(props: BankTransactionDrawerProps) {
  const modeOptions = ['Cheque', 'NEFT/RTGS', 'UPI'];
  const cameraVoucherInputRef = React.useRef<HTMLInputElement | null>(null);
  const [uploadPhotoAnchorEl, setUploadPhotoAnchorEl] = React.useState<HTMLElement | null>(null);
  const uploadPhotoMenuOpen = Boolean(uploadPhotoAnchorEl);
  const {
    open,
    onClose,
    title,
    saveError,
    saving,
    voucherUploading,
    onSave,
    onSaveAndSend,
    showSaveAndSendButton = false,
    saveAndSendLabel = 'Save & Send',
    onSkip,
    showSkipButton = false,
    skipButtonLabel = 'Skip for now',
    onExtraFooterAction,
    showExtraFooterAction = false,
    extraFooterActionLabel = 'Extra action',
    onHeaderAction,
    headerActionIcon,
    headerActionTitle = 'Action',
    customTopSection,
    customPostAmountSection,
    customAfterAmountSection,
    txnType,
    setTxnType,
    receiptCategory,
    setReceiptCategory,
    legalChargeType = '',
    setLegalChargeType = () => {},
    showLegalChargeType = false,
    hideTxnType,
    hideReceiptCategory,
    eligibleBankAccounts,
    bankAccountId,
    setBankAccountId,
    txnDateYMD,
    setTxnDateYMD,
    amount,
    setAmount,
    showUnitPicker,
    eligibleUnitOptions,
    unitId,
    setUnitId,
    unitNumber,
    setUnitNumber,
    pendingAmountText,
    partyName,
    setPartyName,
    partyNameOptions = [],
    payerBankName,
    setPayerBankName,
    payerBankNameOptions = [],
    onPayerBankClear,
    mode,
    setMode,
    referenceNo,
    setReferenceNo,
    notes,
    setNotes,
    voucherInputRef,
    voucherFile,
    setVoucherFile,
    voucherRequired,
    existingVoucherUrl,
    drawerWidth = 520,
    variant = 'drawer',
    panelSx,
    readOnly = false,
    editableFields,
    onToggleEditField,
  } = props;

  const content = (
    <Box
      sx={{
        p: 2,
        pb: { xs: 'calc(env(safe-area-inset-bottom, 0px) + 72px)', sm: 2 },
        height: '100%',
        overflowY: 'auto',
      }}
    >
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {onHeaderAction && headerActionIcon ? (
              <IconButton size="small" onClick={onHeaderAction} title={headerActionTitle} aria-label={headerActionTitle}>
                {headerActionIcon}
              </IconButton>
            ) : null}
            <Button variant="text" onClick={onClose} sx={{ minWidth: 32, px: 1 }}>
              ×
            </Button>
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {saveError ? (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {saveError}
          </Alert>
        ) : null}

        <Stack spacing={1.5}>
          {customTopSection ? customTopSection : null}
          {!hideTxnType ? (
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 3,
                bgcolor: '#f8fafc',
                borderColor: 'rgba(15, 23, 42, 0.12)',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                  <span>Transaction Type</span>
                  {readOnly && onToggleEditField ? (
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => onToggleEditField('txnType')}
                      sx={{ minWidth: 0, p: 0.25, lineHeight: 0 }}
                    >
                      <EditOutlinedIcon fontSize="inherit" />
                    </Button>
                  ) : null}
                </Stack>
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={txnType}
                onChange={(_, v) => {
                  if (!v) return;
                  setTxnType(v as TxnType);
                  if (v === 'payment') setReceiptCategory('customer');
                }}
                disabled={readOnly && !editableFields?.txnType}
                sx={{ flexWrap: 'wrap', gap: 1 }}
              >
                {(['receipt', 'payment'] as TxnType[]).map((opt) => (
                  <ToggleButton
                    key={opt}
                    value={opt}
                    sx={{
                      px: 2,
                      py: 0.75,
                      borderRadius: 999,
                      fontWeight: 800,
                      textTransform: 'capitalize',
                      bgcolor: '#ffffff',
                      borderColor: 'rgba(15, 23, 42, 0.12)',
                      '&.Mui-selected': {
                        bgcolor: opt === 'receipt' ? '#e0f2fe' : '#fef9c3',
                        color: '#0f172a',
                      },
                    }}
                  >
                    {opt}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>

              {!hideReceiptCategory && txnType === 'receipt' ? (
                <Box sx={{ mt: 1.25 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                      <span>Receipt For</span>
                      {readOnly && onToggleEditField ? (
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => onToggleEditField('receiptCategory')}
                          sx={{ minWidth: 0, p: 0.25, lineHeight: 0 }}
                        >
                          <EditOutlinedIcon fontSize="inherit" />
                        </Button>
                      ) : null}
                    </Stack>
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    value={receiptCategory}
                    onChange={(_, v) => v && setReceiptCategory(v)}
                    disabled={readOnly && !editableFields?.txnType && !editableFields?.receiptCategory}
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    <ToggleButton
                      value="customer"
                      sx={{
                        px: 2,
                        py: 0.75,
                        borderRadius: 999,
                        fontWeight: 800,
                        bgcolor: '#ffffff',
                        borderColor: 'rgba(15, 23, 42, 0.12)',
                        '&.Mui-selected': { bgcolor: '#dcfce7', color: '#0f172a' },
                      }}
                    >
                      Customer Payment
                    </ToggleButton>
                    <ToggleButton
                      value="gst"
                      sx={{
                        px: 2,
                        py: 0.75,
                        borderRadius: 999,
                        fontWeight: 800,
                        bgcolor: '#ffffff',
                        borderColor: 'rgba(15, 23, 42, 0.12)',
                        '&.Mui-selected': {
                          bgcolor: '#dbeafe',
                          color: '#0f172a',
                          borderColor: '#93c5fd',
                        },
                      }}
                    >
                      GST Payment
                    </ToggleButton>
                    <ToggleButton
                      value="other"
                      sx={{
                        px: 2,
                        py: 0.75,
                        borderRadius: 999,
                        fontWeight: 800,
                        bgcolor: '#ffffff',
                        borderColor: 'rgba(15, 23, 42, 0.12)',
                        '&.Mui-selected': {
                          bgcolor: '#dbeafe',
                          color: '#0f172a',
                          borderColor: '#93c5fd',
                        },
                      }}
                    >
                      Other
                    </ToggleButton>
                    <ToggleButton
                      value="advocate"
                      sx={{
                        px: 2,
                        py: 0.75,
                        borderRadius: 999,
                        fontWeight: 800,
                        bgcolor: '#ffffff',
                        borderColor: 'rgba(15, 23, 42, 0.12)',
                        '&.Mui-selected': {
                          bgcolor: '#f3e8ff',
                          color: '#0f172a',
                          borderColor: '#c4b5fd',
                        },
                      }}
                    >
                      Advocate
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              ) : null}

            </Paper>
          ) : null}

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 3,
              bgcolor: '#f8fafc',
              borderColor: 'rgba(15, 23, 42, 0.12)',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Account & Date
            </Typography>
            <Stack spacing={1}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                    <span>Bank Account</span>
                    {readOnly && onToggleEditField ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => onToggleEditField('bank')}
                        sx={{ minWidth: 0, p: 0.25, lineHeight: 0 }}
                      >
                        <EditOutlinedIcon fontSize="inherit" />
                      </Button>
                    ) : null}
                  </Stack>
                </Typography>
                {eligibleBankAccounts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No eligible bank accounts for this selection.
                  </Typography>
                ) : (
                  <ToggleButtonGroup
                    exclusive
                    value={bankAccountId}
                    onChange={(_, v) => v && setBankAccountId(v)}
                    disabled={readOnly && !editableFields?.bank}
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    {eligibleBankAccounts.map((b) => (
                      <ToggleButton
                        key={b.id}
                        value={b.id}
                        sx={{
                          px: 2,
                          py: 0.75,
                          borderRadius: 999,
                          fontWeight: 800,
                          bgcolor: '#ffffff',
                          borderColor: 'rgba(15, 23, 42, 0.12)',
                          '&.Mui-selected': {
                            bgcolor: '#dbeafe',
                            color: '#0f172a',
                            borderColor: '#93c5fd',
                          },
                        }}
                      >
                        {b.name || b.bankName || b.id}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                )}
              </Box>
              {showLegalChargeType && bankAccountId ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    Advocate Transaction Type
                  </Typography>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={legalChargeType}
                    onChange={(e) =>
                      setLegalChargeType(
                        String(e.target.value || '') as
                          | ''
                          | 'stamp_duty'
                          | 'registration_charges'
                          | 'agreement_fee'
                          | 'deed_fee'
                      )
                    }
                    disabled={readOnly}
                    placeholder="Select transaction type"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#ffffff',
                        borderColor: 'rgba(15, 23, 42, 0.12)',
                      },
                    }}
                  >
                    <MenuItem value="">Select transaction type</MenuItem>
                    <MenuItem value="stamp_duty">Stamp Duty</MenuItem>
                    <MenuItem value="registration_charges">Registration Charge</MenuItem>
                    <MenuItem value="agreement_fee">Sale Agreement Fee</MenuItem>
                    <MenuItem value="deed_fee">Sale Deed Fee</MenuItem>
                  </TextField>
                </Box>
              ) : null}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Chq Date
                </Typography>
                <TextField
                  size="small"
                  type="date"
                  value={txnDateYMD}
                  onChange={(e) => setTxnDateYMD(e.target.value)}
                  disabled={readOnly}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 999,
                      bgcolor: '#ffffff',
                      borderColor: 'rgba(15, 23, 42, 0.12)',
                    },
                  }}
                />
              </Box>
            </Stack>
          </Paper>

          {customPostAmountSection ? customPostAmountSection : null}

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 3,
              bgcolor: '#f8fafc',
              borderColor: 'rgba(15, 23, 42, 0.12)',
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Amount & Party
              </Typography>
              {pendingAmountText ? (
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                  {pendingAmountText}
                </Typography>
              ) : null}
            </Stack>
            <Stack spacing={1}>
              {showUnitPicker ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    Unit No
                  </Typography>
                  <Autocomplete
                    options={eligibleUnitOptions}
                    getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.unitNumber)}
                    value={
                      unitId
                        ? eligibleUnitOptions.find((u) => u.id === unitId) || null
                        : unitNumber
                          ? ({ id: '', unitNumber } as any)
                          : null
                    }
                    onChange={(_, v: any) => {
                      if (!v) {
                        setUnitId('');
                        setUnitNumber('');
                        return;
                      }
                      if (typeof v === 'string') {
                        setUnitId('');
                        setUnitNumber(v);
                        return;
                      }
                      setUnitId(String(v.id || ''));
                      setUnitNumber(String(v.unitNumber || ''));
                    }}
                    disabled={readOnly}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Select unit"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 999,
                            bgcolor: '#ffffff',
                            borderColor: 'rgba(15, 23, 42, 0.12)',
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              ) : null}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  Party Name
                </Typography>
                <Autocomplete
                  freeSolo
                  options={partyNameOptions}
                  value={partyName || null}
                    onInputChange={(_, v) => setPartyName(v || '')}
                    disabled={readOnly}
                    renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select or type"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 999,
                          bgcolor: '#ffffff',
                          borderColor: 'rgba(15, 23, 42, 0.12)',
                        },
                      }}
                    />
                  )}
                  />
                </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                    <span>Amount</span>
                    {readOnly && onToggleEditField ? (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => onToggleEditField('amount')}
                        sx={{ minWidth: 0, p: 0.25, lineHeight: 0 }}
                      >
                        <EditOutlinedIcon fontSize="inherit" />
                      </Button>
                    ) : null}
                  </Stack>
                </Typography>
                <TextField
                  size="small"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={readOnly && !editableFields?.amount}
                  fullWidth
                  inputMode="numeric"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 999,
                      bgcolor: '#ffffff',
                      borderColor: 'rgba(15, 23, 42, 0.12)',
                    },
                  }}
                />
              </Box>
              {txnType === 'receipt' && (receiptCategory === 'customer' || receiptCategory === 'gst' || receiptCategory === 'advocate') ? (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    Payer Bank
                  </Typography>
                  <Autocomplete
                    freeSolo
                    options={payerBankNameOptions}
                    value={payerBankName || null}
                    onInputChange={(_, v, reason) => {
                      setPayerBankName(v || '');
                      if (reason === 'clear') onPayerBankClear?.();
                    }}
                    disabled={readOnly}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Search or type bank name"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 999,
                            bgcolor: '#ffffff',
                            borderColor: 'rgba(15, 23, 42, 0.12)',
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              ) : null}
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                    Mode
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    value={mode || null}
                    onChange={(_, v) => setMode(v || '')}
                    disabled={readOnly}
                    fullWidth
                    sx={{
                      width: '100%',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                      gap: 0.75,
                      '& .MuiToggleButtonGroup-grouped': {
                        borderRadius: '999px !important',
                        border: '1px solid rgba(15, 23, 42, 0.12) !important',
                        margin: '0 !important',
                      },
                    }}
                  >
                    {modeOptions.map((opt) => (
                      <ToggleButton
                        key={opt}
                        value={opt}
                        sx={{
                          px: 1,
                          py: 1,
                          minWidth: 0,
                          fontWeight: 800,
                          textTransform: 'none',
                          bgcolor: '#ffffff',
                          borderColor: 'rgba(15, 23, 42, 0.12)',
                          '&.Mui-selected': {
                            bgcolor: '#e0f2fe',
                            color: '#0f172a',
                          },
                        }}
                      >
                        {opt}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" component="span">
                        <span>Reference No</span>
                        {readOnly && onToggleEditField ? (
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => onToggleEditField('referenceNo')}
                            sx={{ minWidth: 0, p: 0.25, lineHeight: 0 }}
                          >
                            <EditOutlinedIcon fontSize="inherit" />
                          </Button>
                        ) : null}
                      </Stack>
                    </Typography>
                    <TextField
                      size="small"
                      value={referenceNo}
                      onChange={(e) => setReferenceNo(e.target.value)}
                      disabled={readOnly && !editableFields?.referenceNo}
                      fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 999,
                        bgcolor: '#ffffff',
                        borderColor: 'rgba(15, 23, 42, 0.12)',
                      },
                    }}
                  />
                </Box>
              </Stack>
            </Stack>
          </Paper>

          {customAfterAmountSection ? customAfterAmountSection : null}

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 3,
              bgcolor: '#f8fafc',
              borderColor: 'rgba(15, 23, 42, 0.12)',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Chq / Voucher Photo
              {voucherRequired ? (
                <Typography component="span" color="error" sx={{ ml: 0.75, fontWeight: 800 }}>
                  *
                </Typography>
              ) : null}
            </Typography>
            <Stack spacing={1}>
                <input
                  ref={voucherInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    setVoucherFile(f);
                  }}
                  disabled={readOnly}
                />
                <input
                  ref={cameraVoucherInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    setVoucherFile(f);
                  }}
                  disabled={readOnly}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    variant="outlined"
                    onClick={(e) => setUploadPhotoAnchorEl(e.currentTarget)}
                    disabled={voucherUploading || saving || readOnly}
                  >
                  Upload Photo
                </Button>
                <Menu
                  anchorEl={uploadPhotoAnchorEl}
                  open={uploadPhotoMenuOpen}
                  onClose={() => setUploadPhotoAnchorEl(null)}
                >
                  <MenuItem
                    onClick={() => {
                      setUploadPhotoAnchorEl(null);
                      cameraVoucherInputRef.current?.click();
                    }}
                  >
                    Take Photo
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setUploadPhotoAnchorEl(null);
                      voucherInputRef.current?.click();
                    }}
                  >
                    Upload from Gallery
                  </MenuItem>
                </Menu>
                {voucherFile ? (
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {voucherFile.name}
                  </Typography>
                ) : null}
                {!voucherFile && existingVoucherUrl ? (
                  <Button variant="text" onClick={() => window.open(String(existingVoucherUrl), '_blank', 'noopener,noreferrer')}>
                    View Existing
                  </Button>
                ) : null}
              </Stack>
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 3,
              bgcolor: '#f8fafc',
              borderColor: 'rgba(15, 23, 42, 0.12)',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
              Notes
            </Typography>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Notes
              </Typography>
              <TextField
                size="small"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={readOnly}
                fullWidth
                multiline
                minRows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: '#ffffff',
                    borderColor: 'rgba(15, 23, 42, 0.12)',
                  },
                }}
              />
            </Box>
          </Paper>

          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ mt: 0.5 }}
          >
            {showSkipButton ? (
              <Button
                variant="text"
                onClick={() => onSkip?.()}
                disabled={saving}
              >
                {skipButtonLabel}
              </Button>
            ) : null}
            <Button variant="outlined" onClick={onClose} disabled={saving} sx={{ minWidth: { xs: 112, sm: 96 } }}>
              Cancel
            </Button>
            {showSaveAndSendButton ? (
              <Button variant="outlined" color="success" onClick={() => onSaveAndSend?.()} disabled={saving} sx={{ minWidth: { xs: 132, sm: 120 } }}>
                {saving ? 'Saving…' : saveAndSendLabel}
              </Button>
            ) : null}
            {showExtraFooterAction ? (
              <Button variant="outlined" color="secondary" onClick={() => onExtraFooterAction?.()} disabled={saving} sx={{ minWidth: { xs: 132, sm: 120 } }}>
                {extraFooterActionLabel}
              </Button>
            ) : null}
            <Button
              variant="contained"
              onClick={onSave}
              disabled={saving || (readOnly && !editableFields)}
              sx={{ minWidth: { xs: 112, sm: 96 } }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>
    </Box>
  );

  if (variant === 'panel') {
    return (
      <Box sx={panelSx}>
        {content}
      </Box>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: drawerWidth },
          maxWidth: '100vw',
          top: 'var(--office-appbar-height, 0px)',
          height: 'calc(100vh - var(--office-appbar-height, 0px))',
          borderLeft: '1px solid rgba(0,0,0,0.08)',
        },
      }}
    >
      {content}
    </Drawer>
  );
}
